import { JOB_STATUSES, JOB_TYPES, type JobDto } from "@shelflist/shared";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ListChecks } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { JobStatusBadge } from "@/components/jobs/status-badge";
import { Page } from "@/components/page";
import { ResponsiveTable } from "@/components/responsive-table";
import { EmptyState, LoadingState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { useFormat } from "@/i18n/format";
import { useErrorMessage } from "@/lib/errors";
import { JOBS_PAGE_SIZE, jobDuration, jobsQuery, useJobTypeLabel } from "@/lib/jobs";

/** Filters and page live in the URL, so other pages can link to e.g. `?status=failed`. */
const jobsSearchSchema = z.object({
  status: z.enum(JOB_STATUSES).optional().catch(undefined),
  type: z.enum(JOB_TYPES).optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/admin/jobs/")({
  validateSearch: jobsSearchSchema,
  component: JobsPage,
});

export function JobsTable({ jobs }: { jobs: JobDto[] }) {
  const { t } = useTranslation("jobs");
  const format = useFormat();
  const typeLabel = useJobTypeLabel();
  return (
    <ResponsiveTable
      interactive
      rows={jobs}
      rowKey={(job) => job.id}
      columns={[
        {
          id: "type",
          header: t("columns.type"),
          primary: true,
          cell: (job) => (
            <Link
              to="/admin/jobs/$jobId"
              params={{ jobId: job.id }}
              data-row-link
              className="font-medium underline-offset-4 outline-none hover:underline"
            >
              {typeLabel(job.type)}
            </Link>
          ),
        },
        {
          id: "status",
          header: t("columns.status"),
          cell: (job) => <JobStatusBadge job={job} />,
        },
        {
          id: "createdBy",
          header: t("columns.createdBy"),
          cell: (job) => job.createdBy?.name ?? "—",
        },
        {
          id: "createdAt",
          header: t("columns.createdAt"),
          cell: (job) => format.dateTime(job.createdAt),
        },
        {
          id: "duration",
          header: t("columns.duration"),
          cell: (job) => jobDuration(job, format.duration),
        },
      ]}
    />
  );
}

function JobsPage() {
  const { t } = useTranslation("jobs");
  const errorMessage = useErrorMessage();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { status, type } = search;
  const page = search.page ?? 1;
  const setFilters = (patch: { status?: typeof status; type?: typeof type; page?: number }) =>
    void navigate({
      search: (prev) => ({
        ...prev,
        ...patch,
        page: patch.page && patch.page > 1 ? patch.page : undefined,
      }),
    });
  const { data, error } = useQuery(jobsQuery({ status, type, page }));
  const pages = data ? Math.max(1, Math.ceil(data.total / JOBS_PAGE_SIZE)) : 1;

  return (
    <Page title={t("title")} description={t("description")}>
      <div className="flex flex-wrap items-end gap-4">
        <div className="grid gap-1 text-sm font-medium">
          <Label htmlFor="jobs-status">{t("filters.status")}</Label>
          <NativeSelect
            id="jobs-status"
            className="w-48"
            value={status ?? ""}
            onChange={(event) => {
              setFilters({ status: (event.target.value || undefined) as typeof status, page: 1 });
            }}
          >
            <option value="">{t("filters.all")}</option>
            {JOB_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`status.${value}`)}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="grid gap-1 text-sm font-medium">
          <Label htmlFor="jobs-type">{t("filters.type")}</Label>
          <NativeSelect
            id="jobs-type"
            className="w-48"
            value={type ?? ""}
            onChange={(event) => {
              setFilters({ type: (event.target.value || undefined) as typeof type, page: 1 });
            }}
          >
            <option value="">{t("filters.all")}</option>
            {JOB_TYPES.map((value) => (
              <option key={value} value={value}>
                {t(`types.${value}`)}
              </option>
            ))}
          </NativeSelect>
        </div>
      </div>
      {error ? (
        <p role="alert" className="text-destructive">
          {errorMessage(error)}
        </p>
      ) : null}
      {!data && !error ? <LoadingState /> : null}
      {data && data.jobs.length === 0 ? <EmptyState icon={ListChecks} title={t("empty")} /> : null}
      {data && data.jobs.length > 0 ? <JobsTable jobs={data.jobs} /> : null}
      {data && pages > 1 ? (
        <nav className="flex items-center justify-between" aria-label={t("title")}>
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setFilters({ page: page - 1 })}
          >
            {t("pagination.previous")}
          </Button>
          <span className="text-muted-foreground text-sm">
            {t("pagination.page", { page, pages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pages}
            onClick={() => setFilters({ page: page + 1 })}
          >
            {t("pagination.next")}
          </Button>
        </nav>
      ) : null}
    </Page>
  );
}
